import { Button, Container, Input, toast } from "@medusajs/ui"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { EmptyState, ErrorState, PageHeader, SearchInput } from "@/components/ui"
import { apiFetch } from "@/lib/sdk"
import { Badge, FilterBar, FilterSelect, LoadingCard } from "@/terabizz"

/**
 * Settings → Security log: every event in the write-once security log (staff changes with
 * before/after values, sign-ins and failed sign-ins, every checkout payment attempt, customer
 * account changes, script-policy blocks, checkout script watch). Owners and admins only; viewing
 * and exporting are themselves logged. Filters live in the URL so an investigation can be shared.
 */

type Event = {
  id: string
  seq: string
  created_at: string
  actor_type: string
  actor_id: string | null
  actor_label: string | null
  action: string
  area: string | null
  resource_id: string | null
  result: "ok" | "failed" | "blocked"
  reason: string | null
  ip: string | null
  user_agent: string | null
  country: string | null
  request_id: string | null
  method: string | null
  path: string | null
  status_code: number | null
  changes: Record<string, [unknown, unknown]> | null
  meta: Record<string, unknown> | null
}
type Resp = { events: Event[]; count: number; last_24h: { blocked: number; failed: number; declines: number; failed_sign_ins: number; checkout_script_events: number } }

const PAGE = 50
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10)
const RANGES: [string, string][] = [
  ["1d", "Last 24 hours"],
  ["7d", "Last 7 days"],
  ["30d", "Last 30 days"],
  ["90d", "Last 90 days"],
  ["all", "All time"],
]
const fromFor = (r: string) => (r === "1d" ? new Date(Date.now() - 86_400_000).toISOString() : r === "7d" ? daysAgo(6) : r === "30d" ? daysAgo(29) : r === "90d" ? daysAgo(89) : undefined)

const WHO: [string, string][] = [
  ["staff", "Staff"],
  ["customer", "Customers"],
  ["shopper", "Shoppers"],
  ["system", "System"],
  ["api_key", "API keys"],
]
const AREAS: [string, string][] = [
  ["checkout", "Checkout and payments"],
  ["auth", "Sign-ins"],
  ["customer_account", "Customer accounts"],
  ["security", "Script watch and blocks"],
  ["settings", "Settings"],
  ["content", "Content"],
  ["products", "Products"],
  ["orders", "Orders"],
  ["discounts", "Discounts"],
  ["staff", "Staff"],
]
const ACTIONS: [string, string][] = [
  ["checkout.payment.", "Payment attempts"],
  ["checkout.payment.declined", "Payment declines"],
  ["staff.sign_in", "Staff sign-in attempts"],
  ["customer.", "Customer account events"],
  ["checkout.script", "Checkout script events"],
  ["terify.settings", "Settings changes"],
]

const when = (s: string) => new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" })
const short = (v: unknown) => {
  if (v == null || v === "") return "—"
  const s = typeof v === "string" ? v : JSON.stringify(v)
  return s.length > 240 ? `${s.slice(0, 240)}…` : s
}

export function SecurityLogPage() {
  const [params, setParams] = useSearchParams()
  const get = (k: string) => params.get(k) ?? ""
  const page = Math.max(0, Number(params.get("page") ?? 1) - 1)
  const range = params.get("range") ?? "7d"
  const query = {
    limit: String(PAGE),
    offset: String(page * PAGE),
    ...(get("q") ? { q: get("q") } : {}),
    ...(get("who") ? { actor_type: get("who") } : {}),
    ...(get("result") ? { result: get("result") } : {}),
    ...(get("area") ? { area: get("area") } : {}),
    ...(get("action") ? { action: get("action") } : {}),
    ...(fromFor(range) ? { from: fromFor(range)! } : {}),
  }
  const qs = new URLSearchParams(query).toString()
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["security-log", qs],
    queryFn: () => apiFetch<Resp>(`/admin/terify/security-log?${qs}`),
    placeholderData: keepPreviousData,
  })
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k)
    if (!("page" in patch)) next.delete("page")
    setParams(next, { replace: true })
  }
  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      const base = import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin
      const p = new URLSearchParams(query)
      p.delete("limit")
      p.delete("offset")
      p.set("format", "csv")
      const res = await fetch(`${base}/admin/terify/security-log?${p}`, { credentials: "include", headers: { accept: "text/csv" } })
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message ?? "Export failed")
      const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "security-log.csv"
      const url = URL.createObjectURL(await res.blob())
      const a = Object.assign(document.createElement("a"), { href: url, download: name })
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed")
    } finally {
      setExporting(false)
    }
  }
  const filtered = !!(get("q") || get("who") || get("result") || get("area") || get("action"))
  const count = data?.count ?? 0
  const t = data?.last_24h

  return (
    <>
      <PageHeader
        title="Security log"
        subtitle="Every sign-in, change, payment attempt and blocked script, kept for 2 years. Entries can't be edited or deleted."
        actions={
          <Button size="small" variant="secondary" onClick={exportCsv} isLoading={exporting}>
            Export CSV
          </Button>
        }
      />
      {t ? (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {(
            [
              ["Blocked", t.blocked, { result: "blocked", range: "1d" }],
              ["Failed", t.failed, { result: "failed", range: "1d" }],
              ["Payment declines", t.declines, { action: "checkout.payment.declined", range: "1d" }],
              ["Failed sign-ins", t.failed_sign_ins, { action: "staff.sign_in", result: "blocked", range: "1d" }],
              ["Checkout script alerts", t.checkout_script_events, { action: "checkout.script", range: "1d" }],
            ] as [string, number, Record<string, string>][]
          ).map(([label, n, f]) => (
            <button
              key={label}
              type="button"
              onClick={() => set({ q: null, who: null, area: null, result: null, action: null, ...f })}
              className={`tf-card rounded-[10px] border border-[var(--tb-line)] bg-[var(--tb-surface,#fff)] px-3 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)] ${n > 0 && label !== "Failed" ? "border-[var(--tb-critical,#c4320a)]" : ""}`}
            >
              <span className="block text-[12px] text-[var(--tb-ink-3)]">{label} · 24h</span>
              <span className="block text-[20px] font-semibold tabular-nums text-[var(--tb-ink)]">{n}</span>
            </button>
          ))}
        </div>
      ) : null}
      <AlertRecipients />
      <Container className="tf-card overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 [&>div]:max-w-none">
          <SearchInput value={get("q")} onChange={(v) => set({ q: v || null })} placeholder="Search by email, IP address, order, cart, action or detail" />
        </div>
        <FilterBar>
          <FilterSelect label="Date" value={range} onChange={(v) => set({ range: v === "7d" ? null : v })} options={RANGES} allowAll={false} />
          <FilterSelect label="Who" value={get("who")} onChange={(v) => set({ who: v || null })} options={WHO} />
          <FilterSelect label="Area" value={get("area")} onChange={(v) => set({ area: v || null })} options={AREAS} />
          <FilterSelect label="Type" value={get("action")} onChange={(v) => set({ action: v || null })} options={ACTIONS} />
          <FilterSelect
            label="Result"
            value={get("result")}
            onChange={(v) => set({ result: v || null })}
            options={[
              ["ok", "Done"],
              ["failed", "Failed"],
              ["blocked", "Blocked"],
            ]}
          />
          {filtered && (
            <button type="button" className="txt-compact-small ml-auto text-ui-fg-interactive hover:underline" onClick={() => set({ q: null, who: null, area: null, result: null, action: null })}>
              Clear filters
            </button>
          )}
        </FilterBar>
        {error ? (
          <ErrorState error={error} />
        ) : isLoading ? (
          <div className="p-4">
            <LoadingCard lines={6} />
          </div>
        ) : !data?.events.length ? (
          <EmptyState title="No events found" body={filtered ? "Try a longer date range or clear the filters." : "Events appear here as they happen."} />
        ) : (
          <ul className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={isFetching}>
            {data.events.map((e) => (
              <Row key={e.id} e={e} onFilter={set} />
            ))}
          </ul>
        )}
        {count > 0 && (
          <div className="flex items-center justify-between border-t border-[var(--tb-line)] px-4 py-2.5">
            <span className="txt-compact-small tabular-nums text-[var(--tb-ink-3)]">
              {page * PAGE + 1}–{Math.min(count, (page + 1) * PAGE)} of {count.toLocaleString()}
            </span>
            <div className="flex gap-x-2">
              <Button size="small" variant="secondary" disabled={page === 0} onClick={() => set({ page: page > 1 ? String(page) : null })}>
                Previous
              </Button>
              <Button size="small" variant="secondary" disabled={(page + 1) * PAGE >= count} onClick={() => set({ page: String(page + 2) })}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Container>
    </>
  )
}

function ResultBadge({ r }: { r: Event["result"] }) {
  if (r === "blocked") return <Badge tone="attention">Blocked</Badge>
  if (r === "failed") return <Badge tone="critical">Failed</Badge>
  return <Badge>Done</Badge>
}

function Row({ e, onFilter }: { e: Event; onFilter: (p: Record<string, string | null>) => void }) {
  const [open, setOpen] = useState(false)
  const who = e.actor_label || (e.actor_type === "shopper" ? "Shopper" : e.actor_type === "system" ? "System" : e.actor_id) || e.actor_type
  const gw = (e.meta?.gateway ?? null) as Record<string, unknown> | null
  return (
    <li className="border-t border-[var(--tb-line)] first:border-t-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="grid w-full grid-cols-[1fr_auto] items-start gap-x-3 px-4 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)] sm:grid-cols-[150px_1fr_160px_auto]">
        <span className="hidden text-[12px] tabular-nums text-[var(--tb-ink-3)] sm:block">{when(e.created_at)}</span>
        <span className="min-w-0">
          <span className="block truncate font-mono text-[12.5px] text-[var(--tb-ink)]">{e.action}</span>
          <span className="block truncate text-[12px] text-[var(--tb-ink-3)]">
            {who}
            {e.actor_type !== "staff" ? ` · ${e.actor_type}` : ""}
            {e.reason ? ` · ${e.reason}` : ""}
            {gw?.card_brand ? ` · ${gw.card_brand} •••• ${gw.card_last4 ?? ""}` : ""}
          </span>
          <span className="block text-[12px] tabular-nums text-[var(--tb-ink-3)] sm:hidden">{when(e.created_at)}</span>
        </span>
        <span className="hidden truncate font-mono text-[12px] text-[var(--tb-ink-3)] sm:block">
          {e.ip ?? "—"}
          {e.country ? ` · ${e.country}` : ""}
        </span>
        <span className="pt-0.5">
          <ResultBadge r={e.result} />
        </span>
      </button>
      {open && (
        <div className="mx-4 mb-3 rounded-[8px] bg-[var(--tb-canvas)] px-3 py-2.5 text-[12px] sm:ml-[166px]">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-[var(--tb-ink-3)]">When</dt>
            <dd className="tabular-nums">{new Date(e.created_at).toISOString()}</dd>
            <dt className="text-[var(--tb-ink-3)]">Who</dt>
            <dd className="break-all">
              {e.actor_type} {e.actor_label ? `· ${e.actor_label}` : ""} {e.actor_id ? <span className="font-mono">· {e.actor_id}</span> : null}
            </dd>
            {e.ip ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">IP address</dt>
                <dd>
                  <button type="button" className="font-mono text-ui-fg-interactive hover:underline" onClick={() => onFilter({ q: e.ip, range: "all" })}>
                    {e.ip}
                  </button>
                  {e.country ? ` · ${e.country}` : ""} <span className="text-[var(--tb-ink-3)]">(show everything from this IP)</span>
                </dd>
              </>
            ) : null}
            {e.user_agent ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">Device</dt>
                <dd className="break-all">{e.user_agent}</dd>
              </>
            ) : null}
            {e.method || e.path ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">Request</dt>
                <dd className="break-all font-mono">
                  {e.method} {e.path} {e.status_code ? `→ ${e.status_code}` : ""}
                </dd>
              </>
            ) : null}
            {e.resource_id ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">Record</dt>
                <dd className="break-all font-mono">{e.resource_id}</dd>
              </>
            ) : null}
            {e.request_id ? (
              <>
                <dt className="text-[var(--tb-ink-3)]">Request id</dt>
                <dd className="break-all font-mono">{e.request_id}</dd>
              </>
            ) : null}
            <dt className="text-[var(--tb-ink-3)]">Log entry</dt>
            <dd className="font-mono">
              {e.id} · #{e.seq}
            </dd>
          </dl>
          {e.changes && Object.keys(e.changes).length ? (
            <div className="mt-2.5">
              <div className="mb-1 font-medium text-[var(--tb-ink-2)]">What changed</div>
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="text-left text-[var(--tb-ink-3)]">
                    <th className="w-[28%] py-1 pr-2 font-normal">Field</th>
                    <th className="w-[36%] py-1 pr-2 font-normal">Before</th>
                    <th className="py-1 font-normal">After</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(e.changes).map(([k, v]) => (
                    <tr key={k} className="border-t border-[var(--tb-line)] align-top">
                      <td className="break-all py-1 pr-2 font-mono">{k}</td>
                      <td className="break-all py-1 pr-2 font-mono text-[var(--tb-ink-3)]">{short(Array.isArray(v) ? v[0] : v)}</td>
                      <td className="break-all py-1 font-mono">{short(Array.isArray(v) ? v[1] : "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {e.meta && Object.keys(e.meta).length ? (
            <div className="mt-2.5">
              <div className="mb-1 font-medium text-[var(--tb-ink-2)]">Details</div>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-[6px] bg-[var(--tb-surface,#fff)] p-2 font-mono text-[11.5px]">{JSON.stringify(e.meta, null, 2)}</pre>
            </div>
          ) : null}
        </div>
      )}
    </li>
  )
}

/** Who gets security alert emails: exactly this list, nobody else. Empty = alerts are only logged. */
function AlertRecipients() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ["security-log-settings"], queryFn: () => apiFetch<{ alert_emails: string[] }>("/admin/terify/security-log/settings") })
  const [draft, setDraft] = useState<string | null>(null)
  const value = draft ?? (data?.alert_emails ?? []).join(", ")
  const save = useMutation({
    mutationFn: (emails: string[]) => apiFetch<{ alert_emails: string[] }>("/admin/terify/security-log/settings", { method: "POST", body: JSON.stringify({ alert_emails: emails }) }),
    onSuccess: (r) => {
      qc.setQueryData(["security-log-settings"], r)
      setDraft(null)
      toast.success(r.alert_emails.length ? `Security alerts go to ${r.alert_emails.join(", ")}` : "Security alerts will only be logged")
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't save"),
  })
  const emails = value.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean)
  return (
    <Container className="tf-card mb-4 px-4 py-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[260px] flex-1">
          <span className="mb-1 block text-[13px] font-medium text-[var(--tb-ink)]">Security alert emails</span>
          <Input value={value} onChange={(e) => setDraft(e.target.value)} placeholder="you@example.com" />
          <span className="mt-1 block text-[12px] text-[var(--tb-ink-3)]">Only these addresses get security alerts (for example an unexpected script on checkout). Nobody else is emailed. Separate several with commas.</span>
        </label>
        <Button size="small" variant="secondary" disabled={draft == null} isLoading={save.isPending} onClick={() => save.mutate(emails)}>
          Save
        </Button>
      </div>
    </Container>
  )
}
